/**
 * Every way someone joins a team (docs/ONBOARDING.md J1–J15): join codes, channels, the invite message in plain words
 * (English or Spanish), the problem messages people see on a bad link, and pasted lists (J5).
 * Pure functions: the API and both apps use the same wording.
 */
import type { Invite, InviteChannel, InviteLang, IsoDate, IsoDateTime, Role } from "./model.ts";

/** Letters and digits nobody confuses: no 0/O, 1/I/L. 31 symbols; 6 of them ≈ 887 million codes. */
export const JOIN_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
export const JOIN_CODE_LENGTH = 6;

/** Days an invite link or code stays open (unchanged from the first invite flow). */
export const INVITE_DAYS = 14;

/** Deterministic PRNG (cyrb53 hash seeding mulberry32) for string/number seeds. */
function prng(seed: string | number): () => number {
  const s = String(seed);
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  let a = h1 >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A 6-character join code. Same seed, same code (tests); the API passes random bytes (crypto.getRandomValues), which
 * are used directly with rejection sampling so every symbol is equally likely.
 */
export function joinCode(seed: string | number | Uint8Array): string {
  const A = JOIN_CODE_ALPHABET;
  let out = "";
  if (seed instanceof Uint8Array) {
    const limit = 256 - (256 % A.length); // 248: bytes at or above are skipped (no modulo bias)
    for (const b of seed) {
      if (b >= limit) continue;
      out += A[b % A.length];
      if (out.length === JOIN_CODE_LENGTH) return out;
    }
    // Not enough usable bytes: stretch deterministically from what was given.
    const rest = prng(Array.from(seed).join(","));
    while (out.length < JOIN_CODE_LENGTH) out += A[Math.floor(rest() * A.length)];
    return out;
  }
  const r = prng(seed);
  while (out.length < JOIN_CODE_LENGTH) out += A[Math.floor(r() * A.length)];
  return out;
}

/** What a person typed → the stored code, or null. Case, spaces and dashes don't matter ("abc-234" = "ABC234"). */
export function normalizeJoinCode(input: string): string | null {
  const v = input.toUpperCase().replace(/[\s-]/g, "");
  if (v.length !== JOIN_CODE_LENGTH) return null;
  for (const ch of v) if (!JOIN_CODE_ALPHABET.includes(ch)) return null;
  return v;
}

/** Recommended ways to invite this person, best first. QR, code and a shared-device profile always work. */
export function inviteChannels(p: { email?: string | null; phone?: string | null }): InviteChannel[] {
  const out: InviteChannel[] = [];
  if (p.email) out.push("email");
  if (p.phone) out.push("text");
  out.push("qr", "code", "shared_device");
  return out;
}

/** "maria@gmail.com" → "m…@gmail.com" (J13, audit trail). */
export function maskEmail(email: string): string {
  const [user, domain] = email.split("@");
  if (!user || !domain) return "…";
  return `${user[0]}…@${domain}`;
}
/** "801-555-0142" → "…0142" (audit trail: never the whole number). */
export function maskPhone(phone: string): string {
  const d = phone.replace(/\D/g, "");
  return d.length >= 4 ? `…${d.slice(-4)}` : "…";
}

/** US phone numbers: 10 digits, or 11 starting with 1. Returns "801-555-0142", or null. */
export function normalizeUsPhone(input: string): string | null {
  if (!/^[\d\s().+-]+$/.test(input.trim())) return null;
  let d = input.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  if (d.length !== 10) return null;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Last day of a one-event volunteer's invite: the day after the event (J6). */
export function volunteerEndsOn(eventDate: IsoDate): IsoDate {
  const t = Date.parse(`${eventDate}T00:00:00Z`);
  if (Number.isNaN(t) || !/^\d{4}-\d{2}-\d{2}$/.test(eventDate)) throw new Error(`Not a date: ${eventDate}`);
  return new Date(t + 86400000).toISOString().slice(0, 10);
}

/** The calendar day of an instant in the business's own zone (fix/one-truth: Utah's for every business before). */
function localDay(at: Date, timeZone: string): IsoDate {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(at);
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return `${g("year")}-${g("month")}-${g("day")}`;
}

const firstName = (name?: string) => (name ?? "").trim().split(/\s+/)[0] ?? "";

export interface InviteMessage { text: string; emailSubject: string; emailBody: string }

/**
 * The words the manager sends (by email, or from their own phone as a text: J2). Short: at most three sentences.
 * `lang: "es"` gives the Spanish version (J15).
 */
/** `timeZone`: the business's own (its place's, else its state's: states/index.ts timeZoneOf); the "works until" day is its day. */
export function inviteMessage(invite: Pick<Invite, "name" | "expiresAt" | "endsOn">, o: { orgName: string; link: string; code: string; lang?: InviteLang; timeZone: string }): InviteMessage {
  const name = firstName(invite.name);
  const until = invite.endsOn ?? localDay(new Date(invite.expiresAt), o.timeZone);
  const pretty = (lang: InviteLang) => new Intl.DateTimeFormat(lang === "es" ? "es-US" : "en-US", { timeZone: "UTC", month: "long", day: "numeric" }).format(new Date(`${until}T12:00:00Z`));
  if (o.lang === "es") {
    const hi = name ? `Hola, ${name}: ` : "Hola: ";
    const text = `${hi}${o.orgName} te invitó a su equipo en CleanCleaver. Únete aquí: ${o.link} o abre la app y escribe el código ${o.code}.`;
    return { text, emailSubject: `Únete a ${o.orgName} en CleanCleaver`, emailBody: `${text}\n\nLa invitación vale hasta el ${pretty("es")}.` };
  }
  const hi = name ? `Hi ${name}, ` : "Hi, ";
  const text = `${hi}${o.orgName} invited you to their team on CleanCleaver. Join here: ${o.link} or open the app and type the code ${o.code}.`;
  return { text, emailSubject: `Join ${o.orgName} on CleanCleaver`, emailBody: `${text}\n\nThe invite works until ${pretty("en")}.` };
}

export type InviteProblemCode = "expired" | "used" | "revoked" | "wrong_account";
export interface InviteProblem { code: InviteProblemCode; message: string }

/** The exact words from docs/ONBOARDING.md J11–J14. */
export const INVITE_PROBLEM_MESSAGES = {
  expired: "This invite expired. Ask your manager to send it again.",
  used: "This invite was already used.",
  revoked: "This invite was cancelled.",
  replaced: "This invite was replaced by a newer one. Use the newest message from your manager.",
  wrongAccount: (email: string) => `This invite is for ${maskEmail(email)}. Sign in with that one, or ask your manager to change it.`,
} as const;

/**
 * Why this invite can't be accepted by this person right now, or null when it can. An invite that is already used
 * by this same person returns null too (J12: "If it's theirs: open the app"); the caller then just opens the app.
 * Order: cancelled, used by someone else, expired, wrong account.
 */
export function inviteProblem(
  invite: Pick<Invite, "email" | "expiresAt" | "acceptedAt" | "acceptedBy" | "revokedAt" | "replacedBy" | "endsOn">,
  o: { now: Date | IsoDateTime; userEmail?: string; userId?: string; /** The business's own zone: an event volunteer's `endsOn` is its day. */ timeZone: string },
): InviteProblem | null {
  const now = typeof o.now === "string" ? new Date(o.now) : o.now;
  const mine = (!!o.userId && invite.acceptedBy === o.userId) || (!!o.userEmail && !!invite.email && invite.email.toLowerCase() === o.userEmail.toLowerCase());
  if (invite.revokedAt && !invite.acceptedAt) return { code: "revoked", message: invite.replacedBy ? INVITE_PROBLEM_MESSAGES.replaced : INVITE_PROBLEM_MESSAGES.revoked };
  if (invite.acceptedAt) return mine ? null : { code: "used", message: INVITE_PROBLEM_MESSAGES.used };
  if (invite.expiresAt < now.toISOString() || (invite.endsOn && invite.endsOn < localDay(now, o.timeZone))) return { code: "expired", message: INVITE_PROBLEM_MESSAGES.expired };
  if (invite.email && o.userEmail !== undefined && invite.email.toLowerCase() !== o.userEmail.toLowerCase()) return { code: "wrong_account", message: INVITE_PROBLEM_MESSAGES.wrongAccount(invite.email) };
  return null;
}

/* ─────────────── J5: paste a list ─────────────── */

/** The title a "Lead" invite gives (web "Add someone" → Lead, or "lead" in a pasted list). */
export const LEAD_TITLE = "Lead";

/**
 * Audit #10 (2026-09-29): "Lead: runs a shift when you're out" made plain staff, so the lead saw a line cook's Today.
 * A Lead stays role staff (a shift lead can't invite people or change settings) but is marked a person in charge
 * (Food Code 2-101.11): the "You're in charge" card (who's on shift, the hand-off) and the open problems on Today.
 * Several leads may each be in charge; a hand-off reaches whichever of them is on shift (handoff.ts handoffTarget).
 */
export function invitedInCharge(role: Role, title?: string): boolean {
  return role === "staff" && title?.trim().toLowerCase() === LEAD_TITLE.toLowerCase();
}

/** Role words accepted at the end of a line, mapped to app roles. "lead" stays staff: a shift lead can't invite people. */
export const ROLE_WORDS: Record<string, { role: Role; title: string; volunteer?: boolean }> = {
  worker: { role: "staff", title: "Staff" },
  staff: { role: "staff", title: "Staff" },
  volunteer: { role: "staff", title: "Volunteer", volunteer: true },
  lead: { role: "staff", title: LEAD_TITLE },
  manager: { role: "manager", title: "Manager" },
};

export interface InviteListRow {
  line: number; // 1-based line in the pasted text
  raw: string;
  name: string;
  email?: string;
  phone?: string;
  role: Role;
  title: string;
  volunteer: boolean;
  ok: boolean;
  problem?: string;
}

const EMAIL_IN = /[^\s,;<>()]+@[^\s,;<>()]+/;
const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// US formats: (801) 555-0142, 801-555-0142, 801.555.0142, 8015550142, +1 801 555 0142, 1-801-555-0142. Also catches
// short or long digit runs so they are reported instead of becoming part of the name.
const PHONE_IN = /(?:\+?1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}\b|\+?[\d][\d\s().-]{5,}\d/;

/**
 * One person per line: name, then email or phone (or both), then an optional role word. Blank lines are skipped;
 * every other line comes back as a row, bad ones with `ok: false` and a plain `problem` (never silently dropped).
 */
export function parseInviteList(text: string, opts: { defaultRole?: Role } = {}): InviteListRow[] {
  const def = opts.defaultRole ?? "staff";
  const rows: InviteListRow[] = [];
  const seen = new Set<string>();
  text.split(/\r?\n/).forEach((rawLine, i) => {
    const raw = rawLine.trim();
    if (!raw) return;
    const base: InviteListRow = { line: i + 1, raw, name: "", role: def, title: def === "manager" ? "Manager" : "Staff", volunteer: false, ok: false };
    const emailHit = raw.match(EMAIL_IN);
    const phoneHit = raw.replace(emailHit?.[0] ?? "\u0000", " ").match(PHONE_IN);
    const hits = [emailHit, phoneHit].filter((h): h is RegExpMatchArray => !!h && h.index !== undefined);
    if (!hits.length) {
      rows.push({ ...base, name: clean(raw), problem: "Add an email or a phone number after the name." });
      return;
    }
    // Name: everything before the first contact. Role: whatever is left after the contacts.
    const firstAt = Math.min(...hits.map((h) => (h === phoneHit ? raw.indexOf(h[0]) : h.index!)));
    const name = clean(raw.slice(0, firstAt));
    let rest = raw.slice(firstAt);
    for (const h of hits) rest = rest.replace(h[0], " ");
    const roleText = clean(rest).toLowerCase();
    const row: InviteListRow = { ...base, name };
    const problems: string[] = [];
    if (emailHit) {
      const e = emailHit[0].toLowerCase().replace(/^mailto:/, "");
      if (EMAIL_OK.test(e)) row.email = e;
      else problems.push(`"${emailHit[0]}" doesn't look like an email address.`);
    }
    if (phoneHit) {
      const p = normalizeUsPhone(phoneHit[0]);
      if (p) row.phone = p;
      else problems.push(`"${phoneHit[0].trim()}" doesn't look like a US phone number (10 digits).`);
    }
    if (roleText) {
      const w = ROLE_WORDS[roleText];
      if (w) { row.role = w.role; row.title = w.title; row.volunteer = !!w.volunteer; }
      // Any other word is their job title ("barista", "line cook"), with the list's default role: a title never sets the role
      // (only the role words above and the default the inviter picked can) (owner, 2026-10-08).
      else if (clean(rest).length <= 40) { row.role = def; row.title = titleCase(clean(rest)); }
      else problems.push(`"${clean(rest).slice(0, 40)}…" is too long for a job title (40 letters at most).`);
    }
    if (!name) problems.unshift("Add the person's name first.");
    const key = row.email ?? row.phone;
    if (key && seen.has(key)) problems.push("This person is listed twice.");
    if (key) seen.add(key);
    rows.push(problems.length ? { ...row, problem: problems.join(" ") } : { ...row, ok: true });
  });
  return rows;
}

/** "line cook" → "Line cook" (the first letter only; "BBQ lead" keeps its capitals). */
const titleCase = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/** Trims separators people paste between fields (commas, tabs, semicolons, dashes, angle brackets). */
function clean(s: string): string {
  return s.replace(/[<>]/g, " ").replace(/^[\s,;:|–-]+|[\s,;:|–-]+$/g, "").replace(/\s+/g, " ").trim();
}
